<template>
  <product-list-layout
    v-if="productsStore.products?.maxCount || productsStore.loading"
    :show-filter="showFilter"
    :results-text="resultsText"
    :is-loading="productsStore.loading"
    :content-heading-id="$textDictionary['productSearch.list.contentHeadingId']"
    :content-heading="$textDictionary['productSearch.list.contentHeading']"
    :filters-skip-btn-text="
      $textDictionary['productSearch.filter.filtersSkipBtnText.label']
    "
    :filters-open-btn-text="
      $textDictionary['productSearch.filter.filtersOpenBtnText.label']
    "
    :filters-modal-title="
      $textDictionary['productSearch.filterModal.filtersModalTitle.label']
    "
    :filters-modal-close-icon-text="
      $textDictionary[
        'productSearch.filterModal.filtersModalCloseIconText.label'
      ]
    "
    :filters-modal-apply-btn-text="
      $textDictionary[
        'productSearch.filterModal.filtersModalApplyBtnText.label'
      ]
    "
    :data-insights-index="productsStore.indexName"
    :data-insights-query-id="productsStore.queryId"
  >
    <template #filter>
      <product-search-filter />
    </template>
    <template #actions>
      <PrimeVueSelect
        v-model="productsStore.selectedSortOption"
        :placeholder="
          $textDictionary['searchPage.productFilter.sorting.label'] ?? 'Sorting'
        "
        :options="sortOptions"
        option-label="label"
        option-value="value"
        class="w-full md:w-56"
        variant="filled"
        @change="handleSortChange"
      />
    </template>
    <template #filterTags>
      <TagList
        :tags="formattedFilterFacets"
        :is-small="true"
        style-type="secondary"
        @click="handleTagClick"
        @close="handleTagClick"
      />
    </template>
    <template #list>
      <div ref="productListContainer">
        <ProductSearchList
          :group="relatedProductGroup as any"
          size="three-quarters"
          :stack-size="productsStore.products?.results?.length || 0"
          :generate-detail-link="createProductDetailLink"
          :generate-series-link="getProductSeriesLink"
          :generate-product-position="getProductPosition"
          :generate-product-object-id="getProductObjectId"
          :show-is-loading="productsStore.loading"
        />
        <ProductSearchPagination
          v-if="
            !productsStore.loading &&
            productsStore.products?.results?.length &&
            productsStore.products.results.length > 0
          "
          :current-page="currentPage"
          :total-pages="totalPages"
          :prev-label="$textDictionary['searchPage.pagination.prev.label']"
          :next-label="$textDictionary['searchPage.pagination.next.label']"
          @page-change="handlePageChange"
        />
      </div>
    </template>
  </product-list-layout>
  <div v-else class="search-page-layout__empty-content">
    <div class="search-page-layout__empty-content-text rte-text">
      <LibRichTextRenderer :rich-text="noResultsRichText" />
    </div>
    <tag-list-group
      v-bind="{
        headlineLevel: 4,
        items: [tagListGroupArgs.items[2]]
      }"
    />
  </div>
</template>

<script setup lang="ts">
import { useProductsStore } from '~/stores/productsStore/productsStore';
import {
  ProductListLayout,
  TagList,
  TagListGroup
} from '@hypercodestudio/basler-components';
import ProductSearchFilter from './ProductSearchFilter.vue';
import type { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { useCreateProductDetailLink } from '~/composables/useCreateProductDetailLink';
import ProductSearchList from '~/components/shop/ProductSearchList.vue';
import ProductSearchPagination from './ProductSearchPagination.vue';
import type { HyperlinkInterface } from '@hypercodestudio/basler-components/dist/components/helpers/HyperLink.vue';
import type { TagInterface } from '@hypercodestudio/basler-components/dist/components/elements/taglist/TagList.vue';
const { tagListGroupArgs } = useSearchSuggestions();

import { nextTick, useSearchSuggestions } from '#imports';

interface Props {
  showFilter?: boolean;
  resultsText?: string;
  indexName?: string;
}

withDefaults(defineProps<Props>(), {
  showFilter: false,
  resultsText: '',
  indexName: ''
});

const productsStore = useProductsStore();
const route = useRoute();
const { $textDictionary, $dictionary } = useNuxtApp();
const createProductDetailLink = useCreateProductDetailLink();
const noResultsRichText =
  $dictionary.value['searchPage.products.noResults.label'];

// Sort options for the dropdown
const sortOptions = computed(() => [
  {
    label:
      $textDictionary.value['searchPage.productFilter.sort.orderedQty.label'] ??
      'Quantity (desc)',
    value: 'orderedQty'
  },
  {
    label:
      $textDictionary.value['searchPage.productFilter.sort.priceAsc.label'] ??
      'Price (asc)',
    value: 'priceAsc'
  },
  {
    label:
      $textDictionary.value['searchPage.productFilter.sort.priceDesc.label'] ??
      'Price (desc)',
    value: 'priceDesc'
  }
]);

// Handle sort change
function handleSortChange(event: { value: string }) {
  productsStore.handleSortChange(event.value);
}

// Pagination state - initialize from URL parameter
const currentPage = ref(
  (() => {
    const pageFromQuery = route.query.page;
    if (pageFromQuery) {
      const parsedPage = parseInt(pageFromQuery.toString(), 10);
      if (!isNaN(parsedPage) && parsedPage > 0) {
        return parsedPage;
      }
    }
    return 1;
  })()
);

// Use totalPages from productsStore
const totalPages = computed(() => productsStore.totalPages);

// Flag to prevent duplicate API calls
const isPageChangeInProgress = ref(false);

// Handle page change event from pagination component
async function handlePageChange(page: number) {
  // Set flag to prevent the route watcher from making duplicate API calls
  isPageChangeInProgress.value = true;

  currentPage.value = page;
  productsStore.loading = true;

  // Update route with page parameter without triggering search
  const router = useRouter();
  await router.push({
    query: {
      ...route.query,
      page: page.toString()
    }
  });

  // Scroll to the top of the product list
  const container = document.getElementById('globalSearchPageContainer');
  if (container) {
    container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Manually fetch the products for the selected page
  await productsStore.fetchProductsByPage(page);

  // Reset the flag after everything is done
  isPageChangeInProgress.value = false;
}

const getProductSeriesLink = (
  decoratedProduct: DecoratedProduct
): HyperlinkInterface | undefined => {
  const product = relatedProductGroup.value.products.find(
    (p) => p.sku === decoratedProduct.sku
  );
  // Use a type guard to check if seriesLink exists on the product
  if (product && 'seriesLink' in product && product.seriesLink) {
    return product.seriesLink as HyperlinkInterface;
  }
  return undefined;
};

const getProductPosition = (decoratedProduct: DecoratedProduct): string => {
  const product = relatedProductGroup.value.products.find(
    (p) => p.sku === decoratedProduct.sku
  );
  // Use a type guard to check if seriesLink exists on the product
  if (product && 'position' in product && product.position) {
    return String(product.position);
  }
  return '';
};

const getProductObjectId = (decoratedProduct: DecoratedProduct): string => {
  const product = relatedProductGroup.value.products.find(
    (p) => p.sku === decoratedProduct.sku
  );
  // Use a type guard to check if seriesLink exists on the product
  if (product && 'uid' in product && product.uid) {
    return String(product.uid);
  }
  return '';
};

/**
 * Format active filters for display in the TagList component
 * This computed property is reactive to both store filters and route query changes
 * Only shows filters that are available from Algolia for product search
 */
const formattedFilterFacets = computed<TagInterface[]>(() => {
  let tags = [];

  // Get the list of available filter names from Algolia response
  const availableFilterNames =
    productsStore.products?.filters?.map((filter) => filter.name) || [];

  // Get active filters from store, but also check route query parameters for initial load
  let activeFilters = { ...productsStore.filters };

  // If store filters are empty but we have query parameters, use those
  // This handles the case when the page loads with filter query parameters
  // BUT only include filters that are available from Algolia
  if (
    Object.keys(activeFilters).length === 0 ||
    !productsStore.products?.filters
  ) {
    const routeFilters: Record<string, string[]> = {};
    for (const key in route.query) {
      // Skip non-filter parameters
      if (['page', 'q', 'type', 'sort'].includes(key)) {
        continue;
      }

      // Only include filters that are available from Algolia for product search
      // This ensures we only show tags for filters that are actually supported by the product search
      if (!availableFilterNames.includes(key)) {
        continue;
      }

      const value = route.query[key];
      if (value) {
        routeFilters[key] = Array.isArray(value)
          ? (value.map((v) => v?.toString()).filter(Boolean) as string[])
          : [value.toString()];
      }
    }
    activeFilters = { ...activeFilters, ...routeFilters };
  }

  // Filter activeFilters to only include filters that are available from Algolia
  const algoliaOnlyFilters: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(activeFilters)) {
    if (availableFilterNames.includes(key)) {
      algoliaOnlyFilters[key] = value;
    }
  }

  activeFilters = algoliaOnlyFilters;

  // If we still don't have any filters or available filter definitions, return empty
  if (Object.keys(activeFilters).length === 0) {
    return [];
  }

  // Get all available filters to access their labels and min/max values
  const availableFilters =
    productsStore.products?.filters?.reduce<Record<string, any>>(
      (acc, filter) => {
        acc[filter.name] = filter;
        return acc;
      },
      {}
    ) || {};

  // Process each active filter
  for (const [filterName, filterValues] of Object.entries(activeFilters)) {
    if (!filterValues || filterValues.length === 0) {
      continue;
    }

    // Get the filter label (use the filter name if no label is available)
    let filterLabel = `productSearchFilter.${
      availableFilters[filterName]?.name || filterName
    }.label`;
    filterLabel = $textDictionary.value[filterLabel] || filterName;

    // Handle range filters (price and _range)
    if (
      (filterName.includes('price') || filterName.includes('_range')) &&
      filterValues.length === 2
    ) {
      // Get the min and max values from the filter
      const min = filterValues[0];
      const max = filterValues[1];

      // Get the default min and max values from the available filter
      const availableFilter = availableFilters[filterName];
      const defaultMin = availableFilter
        ? getMinValue(availableFilter.values)
        : min;
      const defaultMax = availableFilter
        ? getMaxValue(availableFilter.values)
        : max;

      // Only show the filter if the range is different from the default min/max
      if (min != defaultMin || max != defaultMax) {
        // Format currency for price filters
        const formattedMin = filterName.includes('price') ? `€${min}` : min;
        const formattedMax = filterName.includes('price') ? `€${max}` : max;

        tags.push({
          text: `${filterLabel}: ${formattedMin} - ${formattedMax}`,
          element: 'button' as const,
          showCloseIcon: true,
          id: filterName
        });
      }
    } else {
      // Handle regular filters - these are always displayed since they're explicitly selected
      for (const value of filterValues) {
        tags.push({
          text: `${filterLabel}: ${value}`,
          element: 'button' as const,
          showCloseIcon: true,
          id: `${filterName}_${value}`
        });
      }
    }
  }

  // Filter out any tags with undefined labels
  tags = tags.filter((tag) => {
    return (
      !tag.text.includes('undefined:') && !tag.text.startsWith('undefined')
    );
  });

  return tags;
});

/**
 * Get minimum value from filter values
 */
function getMinValue(values: { value: string; count: number }[]): string {
  if (!values || values.length === 0) {
    return '0';
  }
  const numericValues = values
    .map((v) => Number(v.value))
    .filter((v) => !isNaN(v));
  return numericValues.length > 0 ? Math.min(...numericValues).toString() : '0';
}

/**
 * Get maximum value from filter values
 */
function getMaxValue(values: { value: string; count: number }[]): string {
  if (!values || values.length === 0) {
    return '100';
  }
  const numericValues = values
    .map((v) => Number(v.value))
    .filter((v) => !isNaN(v));
  return numericValues.length > 0
    ? Math.max(...numericValues).toString()
    : '100';
}

/**
 * Handle tag click event from TagList
 * Only handles filters that are available from Algolia for product search
 */
function handleTagClick(tag: any): void {
  if (!tag || !tag.id) {
    return;
  }

  // Reset current page to 1 when filters change
  currentPage.value = 1;

  // Get the list of available filter names from Algolia response
  const availableFilterNames =
    productsStore.products?.filters?.map((filter) => filter.name) || [];

  // Get all active filters - check both store and route query parameters
  let activeFilters = { ...productsStore.filters };

  // If store filters are empty, get them from route query parameters
  // BUT only include filters that are available from Algolia
  if (Object.keys(activeFilters).length === 0) {
    for (const key in route.query) {
      if (['page', 'q', 'type', 'sort'].includes(key)) {
        continue;
      }

      // Only include filters that are available from Algolia for product search
      if (!availableFilterNames.includes(key)) {
        continue;
      }

      const value = route.query[key];
      if (value) {
        activeFilters[key] = Array.isArray(value)
          ? (value.map((v) => v?.toString()).filter(Boolean) as string[])
          : [value.toString()];
      }
    }
  }

  // Filter activeFilters to only include filters that are available from Algolia
  const algoliaOnlyFilters: Record<string, string[]> = {};
  for (const [key, value] of Object.entries(activeFilters)) {
    if (availableFilterNames.includes(key)) {
      algoliaOnlyFilters[key] = value;
    }
  }

  activeFilters = algoliaOnlyFilters;

  // Check if this is a range filter (tag.id matches exactly a filter name)
  if (
    activeFilters[tag.id] &&
    (tag.id.includes('price') || tag.id.includes('_range')) &&
    activeFilters[tag.id].length === 2
  ) {
    // This is a range filter - remove the entire filter
    productsStore.handleFormValueChange(tag.id, []);
  } else {
    // This is a regular filter - find the filter name and value
    // Iterate through all active filters to find the matching value
    for (const [filterName, values] of Object.entries(activeFilters)) {
      if (Array.isArray(values)) {
        for (const value of values) {
          // Check if this tag represents this filter value
          const expectedIds = [`${filterName}_${value}`, filterName];

          if (expectedIds.includes(tag.id)) {
            // Found the matching filter and value

            // Create a new array without the value to be removed
            const newValues = values.filter((v) => v !== value);

            // Update the filter with the remaining values (or empty array if none left)
            // This will update both the store and the URL
            productsStore.handleFormValueChange(filterName, newValues);
            return;
          }
        }
      }
    }
  }
}

// Route watching is now handled by the parent SearchPage component
// This component only needs to react to page changes for pagination
watch(
  () => route.query.page,
  (newPage) => {
    // Skip API calls if the page change is already being handled by handlePageChange
    if (isPageChangeInProgress.value) {
      return;
    }

    // Update current page from route if available
    if (newPage) {
      const parsedPage = parseInt(newPage.toString(), 10);
      if (!isNaN(parsedPage) && parsedPage > 0) {
        currentPage.value = parsedPage;
      } else {
        currentPage.value = 1;
      }
    } else {
      currentPage.value = 1;
    }
  }
);

// watch(
//   [() => route.params],
//   () => productsStore.handleRouteChange(route.params),
//   {
//     immediate: true
//   }
// );

/**
 * Handle load more button click
 */
/**
 * Create a RelatedProductGroupModel from the products in productsStore
 */
const relatedProductGroup = computed(() => {
  // Make sure we have products before trying to map them
  const productResults = productsStore.products?.results || [];

  // Log the number of products for debugging
  // console.log(`ProductSearch: Found ${productResults.length} products`);

  return {
    tabId: 'search-results',
    title: 'Search Results',
    groupTitle: 'Search Results',
    queryFilter: {},
    products: productResults.map((product, index) => {
      // Calculate position based on current page and index within the page
      // Position is 1-based and represents the product's position in the overall search results
      const position = (currentPage.value - 1) * 9 + index + 1;

      // Log each product for debugging
      // console.log(
      //   `ProductSearch: Processing product ${product.id} (${product.sku}) at position ${position}`
      // );
      return {
        uid: product.id,
        sku: product.sku,
        name: product.name,
        position: position,
        seriesLink: product.seriesLink ?? undefined,
        // Add other required properties that might be needed by the component
        stock_status: 'IN_STOCK',
        url_key: product.id,
        __typename: 'SimpleProduct'
      };
    })
  };
});
</script>

<style scoped>
.sort-option {
  min-width: 190px;
}
</style>

<style scoped>
.product-grid__items {
  width: 100%;
}

.load-more {
  margin-top: 2rem;
  text-align: center;
}

.no-results {
  padding: 2rem;
  text-align: center;
  font-size: 1.2rem;
  color: #666;
}

/* Pagination styles */
:deep(.product-list-pagination) {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
}
</style>
